import { selectCurrentHotelId } from 'accessors'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoBar from 'components/ReusableComponents/InfoBar'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table from 'components/ReusableComponents/Table'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import hotelApi from 'services/api/hotel'

import { generateShortNameAndBrand } from '../../../actions/index'
import styles from './InHouseMessagingPerformance.module.scss'
import InHouseTitleRow from './InHouseTitleRow'

const InHouseMessagingPerformance = () => {
  const [isHovering, setIsHovering] = useState(false)
  const [hoverClass, setHoverClass] = useState('')
  const [rowIndex, setRowIndex] = useState(0)

  useEffect(() => {
    if (isHovering) {
      const row = document.getElementById('messageTable').getElementsByTagName('tr')[
        rowIndex
      ]
      const e = row.getElementsByClassName(hoverClass)
      e[0].classList.add(styles.hovered)
      e[1].classList.add(styles.hovered)
    } else {
      document.querySelectorAll(`.${styles.hovered}`).forEach(function (e) {
        e.classList.remove(`${styles.hovered}`)
      })
    }
  }, [isHovering, hoverClass, rowIndex])

  const [isLoading, setIsLoading] = useState(true)
  const [sortDecreasing, setSortDecreasing] = useState(false)
  const [sortByIndex, setSortByIndex] = useState(3)
  const [data, setData] = useState([])
  const subsidiaryId = useSelector(selectCurrentHotelId)
  const groupName = useSelector(s => s.hotelDetails.groupName)

  useEffect(() => {
    setIsLoading(true)
    hotelApi
      .messageResponseTimes()
      .then(res => {
        setData(res)
      })
      .catch(err => {
        console.warn(err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [subsidiaryId])

  function handleTitleCellClick(rowIndex, sortByIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
    getSortedResults(data, rowIndex, sortDecreasing)
  }

  function getSortedResults(data, sortBy, sortDecreasing) {
    const titleRowNames = [
      'avgYesterdayResponseSeconds',
      'avgWeekToDateResponseSeconds',
      'avgLastWeekResponseSeconds',
      'yesterdayRespondedMessageCount',
      'weekToDateRespondedMessageCount',
      'lastWeekRespondedMessageCount',
      'yesterdayNonRespondedMessageCount',
      'weekToDateNonRespondedMessageCount',
      'lastWeekNonRespondedMessageCount'
    ]

    const sortByProp = titleRowNames[sortBy]

    const sortedData = data.sort((a, b) => {
      return b[sortByProp] - a[sortByProp]
    })
    return sortDecreasing ? sortedData : sortedData.reverse()
  }

  const calResponsedMsgColor = (res, nonRes) => {
    if (!nonRes) nonRes = 0
    if (nonRes === 0) return styles.green
    return (res / nonRes) * 100 > 49 ? styles.green : styles.red
  }
  const calAvgResTime = time => {
    return time > 900 ? styles.red : styles.green
  }

  const calTimer = sec => {
    if (isNaN(sec)) {
      sec = 0
    }

    let hour = Math.floor(sec / 3600)
    let remainingSeconds = sec % 3600
    let minutes = Math.floor(remainingSeconds / 60)
    remainingSeconds = remainingSeconds % 60

    if (hour > 0) {
      return hour + 'h ' + minutes + 'm '
    } else if (minutes > 0) {
      return minutes + 'm ' + remainingSeconds + 's'
    } else {
      return remainingSeconds + 's'
    }
  }

  const hotelName = ({ hotelName }) => {
    const { brand, shortName } = generateShortNameAndBrand(hotelName)
    return (
      <th className={styles.hotelCell}>
        <p className={styles.hotelBrand}>{brand}</p>
        <p className={styles.shortHotelName}>{shortName}</p>
      </th>
    )
  }
  return (
    <div className={styles.tableHolder}>
      {isLoading && <LoadingIndicator />}
      {!isLoading && (
        <>
          <CustomHeader width={1080} title="In-house messaging performance" />
          <InfoBar
            text="This table indicates average response times and the count of responded in-property messages per hotel by your staff."
            marginTop={-20}
            marginBottom={20}
          />
          <table className={styles.table} id="performanceTable">
            <thead>
              <tr className={styles.months}>
                <th className={styles.controlsTh}>{groupName}</th>
                <th colSpan={4} className={styles.controlsTh}>
                  <td>AVG RESPONSE TIME</td>
                  <td>RESPONDED MESSAGES</td>
                </th>
              </tr>
              <tr className={styles.headerBottom}>
                <th className={styles.firstSubtitle}>Hotel name</th>
                <td colSpan={2} className={styles.subtitle}>
                  <Table
                    widths={[120, 120, 120, 10, 120, 120, 120]}
                    handleTitleCellClick={handleTitleCellClick}
                    sortIndex={sortByIndex}
                    sortDirection={sortDecreasing ? 'down' : 'up'}
                  >
                    <InHouseTitleRow />
                  </Table>
                </td>
              </tr>
            </thead>
            <tbody className={styles.tableBody} id="messageTable">
              {data.map((item, index) => (
                <tr className={styles.row} rowIndex={index} key={index}>
                  {hotelName({ hotelName: item.hotelName })}
                  <td colSpan={2} className={styles.bodyFontStyle}>
                    <table>
                      <tbody className={styles.hoverTable}>
                        <td>
                          <span
                            className={`${calAvgResTime(
                              item.avgLastWeekResponseSeconds
                            )} ${styles.dataFirst}`}
                            onMouseEnter={() => {
                              setIsHovering(true)
                              setHoverClass(styles.dataFirst)
                              setRowIndex(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovering(false)
                              setHoverClass(styles.dataFirst)
                            }}
                          >
                            {calTimer(item.avgLastWeekResponseSeconds)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`${calAvgResTime(
                              item.avgWeekToDateResponseSeconds
                            )} ${styles.dataSecond}`}
                            onMouseEnter={() => {
                              setIsHovering(true)
                              setHoverClass(styles.dataSecond)
                              setRowIndex(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovering(false)
                              setHoverClass(styles.dataSecond)
                            }}
                          >
                            {calTimer(item.avgWeekToDateResponseSeconds)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`${calAvgResTime(
                              item.avgYesterdayResponseSeconds
                            )} ${styles.dataThird}`}
                            onMouseEnter={() => {
                              setIsHovering(true)
                              setHoverClass(styles.dataThird)
                              setRowIndex(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovering(false)
                              setHoverClass(styles.dataThird)
                            }}
                          >
                            {calTimer(item.avgYesterdayResponseSeconds)}
                          </span>
                        </td>
                        <td></td>
                        <td>
                          {donutChart(
                            `${item.lastWeekRespondedMessageCount}, ${
                              item.lastWeekNonRespondedMessageCount +
                              item.lastWeekRespondedMessageCount
                            }`
                          )}
                          <div
                            className={`${calResponsedMsgColor(
                              item.lastWeekRespondedMessageCount,
                              item.lastWeekRespondedMessageCount +
                                item.lastWeekNonRespondedMessageCount
                            )} ${styles.dataFirst}`}
                            onMouseEnter={() => {
                              setIsHovering(true)
                              setHoverClass(styles.dataFirst)
                              setRowIndex(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovering(false)
                              setHoverClass(styles.dataFirst)
                            }}
                          >
                            {item.lastWeekRespondedMessageCount}/
                            {item.lastWeekRespondedMessageCount +
                              item.lastWeekNonRespondedMessageCount}
                          </div>
                        </td>
                        <td>
                          {donutChart(
                            `${item.weekToDateRespondedMessageCount}, ${
                              item.weekToDateNonRespondedMessageCount +
                              item.weekToDateRespondedMessageCount
                            }`
                          )}
                          <div
                            className={`${calResponsedMsgColor(
                              item.weekToDateRespondedMessageCount,
                              item.weekToDateRespondedMessageCount +
                                item.weekToDateNonRespondedMessageCount
                            )} ${styles.dataSecond}`}
                            onMouseEnter={() => {
                              setIsHovering(true)
                              setHoverClass(styles.dataSecond)
                              setRowIndex(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovering(false)
                              setHoverClass(styles.dataSecond)
                            }}
                          >
                            {item.weekToDateRespondedMessageCount}/
                            {item.weekToDateRespondedMessageCount +
                              item.weekToDateNonRespondedMessageCount}
                          </div>
                        </td>
                        <td>
                          {donutChart(
                            `${item.yesterdayRespondedMessageCount}, ${item.yesterdayTotalMessageCount}`
                          )}
                          <div
                            className={`${calResponsedMsgColor(
                              `${item.yesterdayRespondedMessageCount}, ${item.yesterdayTotalMessageCount}`
                            )} ${styles.dataThird}`}
                            onMouseEnter={() => {
                              setIsHovering(true)
                              setHoverClass(styles.dataThird)
                              setRowIndex(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovering(false)
                              setHoverClass(styles.dataThird)
                            }}
                          >
                            {item.yesterdayRespondedMessageCount}/
                            {item.yesterdayTotalMessageCount}
                          </div>
                        </td>
                      </tbody>
                    </table>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

export default InHouseMessagingPerformance

const donutChart = val => {
  try {
    let remaining, percent, circleData, selectClassName
    const firstValue = val.split(',')[0]
    const secondValue = val.split(',')[1]

    if (parseInt(firstValue) === 0 && parseInt(secondValue) === 0) {
      circleData = '0 0'
      selectClassName = styles.donutSegmentGray
    } else if (secondValue > 0) {
      percent = Math.round((firstValue / secondValue) * 100)
      remaining = 100 - percent
      circleData = percent + ' ' + remaining
      selectClassName =
        percent >= remaining ? styles.donutSegment : styles.donutSegmentAlternate
    } else {
      circleData = '0 0'
      selectClassName = styles.donutSegment
    }
    return (
      <div className={styles.donutSvgItem}>
        <svg width="25" height="20" viewBox="0 0 40 40" class="donut">
          <circle cx="20" cy="20" r="15.91549430918954" fill="#fff"></circle>
          <circle
            className={styles.donutRing}
            cx="20"
            cy="20"
            r="15.91549430918954"
            fill="transparent"
            stroke-width="3.5"
          ></circle>
          <circle
            className={selectClassName}
            cx="20"
            cy="20"
            r="15.91549430918954"
            fill="transparent"
            stroke-width="3.5"
            stroke-dasharray={circleData}
            stroke-dashoffset="25"
          ></circle>
        </svg>
      </div>
    )
  } catch (e) {
    console.warn('Error in donutChart')
  }
}
